import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Root as FlightListType } from "./types";

export interface FilterState {
  stops: string[];
  departure_time: string[];
  arrival_time: string[];
  airline: string[];
  priceRange: number[];
  data: FlightListType[];
  filteredData: FlightListType[];
  filteredReturnFlight: FlightListType[];
  returnData: FlightListType[];
}

type FilterPayload = {
  stops: string[];
  departure_time: string[];
  arrival_time: string[];
  airline: string[];
  priceRange: number[];
  duration?: number;
};

const initialState: FilterState = {
  stops: [],
  departure_time: [],
  arrival_time: [],
  priceRange: [],
  airline: [],
  data: [],
  returnData: [],
  filteredData: [],
  filteredReturnFlight: [],
};

const mapStops = (val: number) => {
  switch (val) {
    case 0:
      return "Non stop";
    case 1:
      return "one";
    case 2:
      return "two";
    default:
      return "";
  }
};

function getTimeOfDay(timeString: string) {
  const [hours] = timeString.split(":").map(Number);
  if (hours >= 0 && hours < 6) return "late_night";
  if (hours >= 6 && hours < 12) return "morning";
  if (hours >= 12 && hours < 18) return "mid_evn";
  return "night";
}

export function filterFlights(flightData: any, filters: FilterPayload) {
  return flightData?.filter((flight: any) => {
    // Check airline filter
    if (
      filters.airline.length > 0 &&
      !filters.airline.includes(flight.airline_code) &&
      !filters.airline.includes(flight.airline_name)
    ) {
      return false;
    }

    // Check stops filter
    if (
      filters.stops.length > 0 &&
      !filters.stops.includes(mapStops(Number(flight.stop_count)))
    ) {
      return false;
    }

    // Check price range filter
    const price = flight.price_details[0]?.fareDetails.net_fare;
    if (filters.priceRange.length === 2 && price > filters.priceRange[1]) {
      return false;
    }

    // Check departure time filter (if provided, e.g., in format ["08:00-12:00"])
    if (filters.departure_time.length > 0) {
      const departureTime = getTimeOfDay(flight.departure_time); // e.g., "11:00"
      if (!filters.departure_time.includes(departureTime)) return false;
    }

    // Check arrival time filter (if provided, e.g., in format ["12:00-16:00"])
    if (filters.arrival_time.length > 0) {
      const arrivalTime = getTimeOfDay(flight.arrival_time); // e.g., "12:55"
      let isValidArrival = false;
      for (const timeRange of filters.arrival_time) {
        const [start, end] = timeRange.split("-");
        if (arrivalTime >= start && arrivalTime <= end) {
          isValidArrival = true;
          break;
        }
      }
      if (!isValidArrival) return false;
    }

    // If all filters pass, include the flight
    return true;
  });
}

const filterSlice = createSlice({
  name: "filter",
  initialState,
  reducers: {
    storeData(
      state,
      action: PayloadAction<{
        data: FlightListType[];
        field: "data" | "returnData";
      }>
    ) {
      state[action.payload.field] = action.payload.data;
      if (action.payload.field === "data") {
        state.filteredData = action.payload.data;
      }
    },
    storeReturnData(
      state,
      action: PayloadAction<{
        data: FlightListType[];
        field: "data" | "returnData";
      }>
    ) {
      state[action.payload.field] = action.payload.data;
      if (action.payload.field === "returnData") {
        state.filteredReturnFlight = action.payload.data;
      }
    },
    applyFilter(
      state,
      action: PayloadAction<FilterPayload & { onwardFlight?: FlightListType[] }>
    ) {
      const hasActiveFilters = Object.entries(action.payload).some(
        ([key, value]) => {
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          return value !== undefined && value !== null;
        }
      );

      if (!hasActiveFilters) {
        state.filteredData = state.data;
        return;
      }

      if (
        action.payload.onwardFlight &&
        action.payload.onwardFlight.length > 0
      ) {
        let filteredData = filterFlights(
          action.payload.onwardFlight,
          action.payload
        );
        state.filteredData = filteredData;
      }
    },
    applyReturnFilter(
      state,
      action: PayloadAction<FilterPayload & { returnFlight?: FlightListType[] }>
    ) {
      const hasActiveFilters = Object.entries(action.payload).some(
        ([key, value]) => {
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          return value !== undefined && value !== null;
        }
      );

      if (!hasActiveFilters) {
        state.filteredReturnFlight = state.returnData;
        return;
      }

      if (
        action.payload.returnFlight &&
        action.payload.returnFlight.length > 0
      ) {
        let filteredReturnFlight = filterFlights(
          action.payload.returnFlight,
          action.payload
        );
        state.filteredReturnFlight = filteredReturnFlight;
      }
    },
    setReturnFlight(state, action: PayloadAction<FlightListType[]>) {
      state.filteredReturnFlight = action.payload;
    },
    resetFilter(state) {
      Object.assign(state, {
        ...initialState,
        data: state.data,
        returnData: state.returnData,
        filteredData: state.data,
        filteredReturnFlight: state.returnData,
      });
    },
  },
});

export const {
  storeData,
  applyFilter,
  applyReturnFilter,
  resetFilter,
  storeReturnData,
  setReturnFlight,
} = filterSlice.actions;
export default filterSlice.reducer;
